import React from 'react';
import './Article.css';

const DatabaseShardingArticle: React.FC = () => {
  return (
    <article className="article">
      <h1 className="article-title">Database Sharding: A Complete Guide</h1>
      
      <div className="article-meta">
        <span className="article-date">Updated: January 24, 2026</span>
        <span className="article-reading-time">10 min read</span>
      </div>

      <section className="article-section">
        <h2>What is Database Sharding?</h2>
        <p>
          Database sharding is a technique for horizontally partitioning data across multiple 
          database instances (shards). Each shard contains a subset of the total data, allowing 
          the database to scale beyond the capacity of a single machine.
        </p>
      </section>

      <section className="article-section">
        <h2>Why Use Sharding?</h2>
        <p>
          As applications grow, a single database server can become a bottleneck. Sharding 
          addresses several critical challenges:
        </p>
        <ul>
          <li><strong>Scale Beyond Single Machine Limits:</strong> Distribute data across multiple servers</li>
          <li><strong>Improve Query Performance:</strong> Smaller datasets mean faster queries</li>
          <li><strong>Increase Write Throughput:</strong> Parallel writes across shards</li>
          <li><strong>Geographic Distribution:</strong> Place data closer to users</li>
          <li><strong>Cost Efficiency:</strong> Use commodity hardware instead of expensive servers</li>
        </ul>
      </section>

      <section className="article-section">
        <h2>Sharding Strategies</h2>

        <h3>1. Range-Based Sharding</h3>
        <p>
          Data is partitioned based on ranges of a shard key. For example, users A-M on one 
          shard, N-Z on another.
        </p>
        <div className="code-block">
          <code>
            Shard 1: user_id 1-1000<br/>
            Shard 2: user_id 1001-2000<br/>
            Shard 3: user_id 2001-3000
          </code>
        </div>
        <div className="info-box">
          <strong>Pros:</strong> Simple to implement, good for range queries<br/>
          <strong>Cons:</strong> Can lead to uneven distribution (hotspots)
        </div>

        <h3>2. Hash-Based Sharding</h3>
        <p>
          A hash function is applied to the shard key to determine which shard stores the data.
        </p>
        <div className="code-block">
          <code>
            shard_number = hash(user_id) % number_of_shards
          </code>
        </div>
        <div className="info-box">
          <strong>Pros:</strong> Even distribution of data<br/>
          <strong>Cons:</strong> Range queries are difficult, adding shards is complex
        </div>

        <h3>3. Geographic/Location-Based Sharding</h3>
        <p>
          Data is partitioned by geographic location, placing data close to where it's accessed.
        </p>
        <div className="code-block">
          <code>
            Shard 1: US users<br/>
            Shard 2: EU users<br/>
            Shard 3: Asia users
          </code>
        </div>

        <h3>4. Directory-Based Sharding</h3>
        <p>
          A lookup service maintains a mapping between shard keys and physical shards. Provides 
          maximum flexibility but adds complexity.
        </p>
      </section>

      <section className="article-section">
        <h2>Challenges of Sharding</h2>

        <h3>Complexity</h3>
        <p>
          Sharding adds significant complexity to your application architecture. You need to 
          handle shard key selection, routing logic, and cross-shard operations.
        </p>

        <h3>Cross-Shard Queries</h3>
        <p>
          Queries that span multiple shards are expensive and complex. They require querying 
          multiple databases and aggregating results in the application layer.
        </p>

        <h3>Rebalancing</h3>
        <p>
          As data grows unevenly, you may need to rebalance shards by moving data between them. 
          This is a complex operation that requires careful planning.
        </p>

        <h3>Data Consistency</h3>
        <p>
          Maintaining ACID properties across shards is challenging. Distributed transactions 
          are complex and can impact performance.
        </p>
      </section>

      <section className="article-section">
        <h2>Choosing a Shard Key</h2>
        <p>
          The shard key is critical to sharding success. A good shard key should:
        </p>
        <ul>
          <li>Provide even distribution of data</li>
          <li>Minimize cross-shard queries</li>
          <li>Support application access patterns</li>
          <li>Be immutable (changing shard keys is expensive)</li>
          <li>Have high cardinality (many possible values)</li>
        </ul>
        <div className="warning-box">
          <strong>⚠️ Warning:</strong> A poor shard key choice can lead to hotspots, 
          uneven load, and degraded performance. Choose carefully!
        </div>
      </section>

      <section className="article-section">
        <h2>Sharding Implementation</h2>

        <h3>Application-Level Sharding</h3>
        <p>
          The application code determines which shard to query. Provides maximum control but 
          requires application changes.
        </p>

        <h3>Middleware/Proxy Layer</h3>
        <p>
          A middleware layer handles routing to appropriate shards. Examples include Vitess 
          (MySQL), Citus (PostgreSQL), and MongoDB's built-in sharding.
        </p>

        <h3>Database-Native Sharding</h3>
        <p>
          Some databases have built-in sharding support (e.g., MongoDB, Cassandra). This 
          simplifies implementation but may lock you into a specific database.
        </p>
      </section>

      <section className="article-section">
        <h2>Alternatives to Sharding</h2>
        <p>
          Before implementing sharding, consider these alternatives:
        </p>
        <ul>
          <li><strong>Vertical Scaling:</strong> Upgrade to a more powerful server</li>
          <li><strong>Read Replicas:</strong> Offload read traffic to replica databases</li>
          <li><strong>Caching:</strong> Use Redis/Memcached to reduce database load</li>
          <li><strong>Archiving:</strong> Move old data to separate storage</li>
          <li><strong>Optimize Queries:</strong> Index optimization and query tuning</li>
        </ul>
      </section>

      <section className="article-section">
        <h2>Best Practices</h2>
        <ul>
          <li>Start with a single database and shard only when necessary</li>
          <li>Choose your shard key carefully based on access patterns</li>
          <li>Plan for rebalancing from the beginning</li>
          <li>Monitor shard distribution and performance metrics</li>
          <li>Implement proper error handling for shard failures</li>
          <li>Document your sharding strategy thoroughly</li>
          <li>Consider using a proven sharding middleware</li>
        </ul>
      </section>

      <section className="article-section">
        <h2>Conclusion</h2>
        <p>
          Database sharding is a powerful technique for scaling databases horizontally, but it 
          comes with significant complexity. Before implementing sharding, exhaust simpler 
          scaling options. If sharding is necessary, invest time in choosing the right strategy 
          and shard key for your use case. Proper planning and implementation will help you 
          build a scalable system that can grow with your application's needs.
        </p>
      </section>
    </article>
  );
};

export default DatabaseShardingArticle;
